import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useCreateTicket } from '@/hooks/use-tickets';
import { cn } from '@/lib/utils';
import type { TicketType, TaskPriority } from 'shared';
import {
  Loader2,
  Plus,
  MessageSquare,
  Ticket,
} from 'lucide-react';

const ticketTypeColors: Record<string, string> = {
  bug: 'bg-red-500/10 text-red-400',
  change_request: 'bg-blue-500/10 text-blue-400',
  general_inquiry: 'bg-purple-500/10 text-purple-400',
  approval_request: 'bg-yellow-500/10 text-yellow-400',
};

const ticketStatusColors: Record<string, string> = {
  open: 'bg-green-500/10 text-green-400',
  in_progress: 'bg-blue-500/10 text-blue-400',
  resolved: 'bg-purple-500/10 text-purple-400',
  closed: 'bg-gray-500/10 text-gray-400',
};

const ticketPriorityColors: Record<string, string> = {
  low: 'bg-gray-500/10 text-gray-400',
  medium: 'bg-blue-500/10 text-blue-400',
  high: 'bg-orange-500/10 text-orange-400',
  urgent: 'bg-red-500/10 text-red-400',
};

interface ProjectTicketsTabProps {
  projectId: string;
  tickets: any[];
  ticketsLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  pagination: { totalPages: number; total: number } | null;
}

export function ProjectTicketsTab({
  projectId,
  tickets,
  ticketsLoading,
  page,
  onPageChange,
  pagination,
}: ProjectTicketsTabProps) {
  const navigate = useNavigate();
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketType, setNewTicketType] =
    useState<TicketType>('general_inquiry');
  const [newTicketPriority, setNewTicketPriority] =
    useState<TaskPriority>('medium');
  const [newTicketDescription, setNewTicketDescription] = useState('');

  const createTicket = useCreateTicket();

  const resetForm = () => {
    setNewTicketSubject('');
    setNewTicketType('general_inquiry');
    setNewTicketPriority('medium');
    setNewTicketDescription('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-secondary)]">
          {pagination?.total || 0} ticket
          {(pagination?.total || 0) !== 1 ? 's' : ''}
        </h3>
        <Button
          size="sm"
          onClick={() => setCreateTicketOpen(true)}
          className="gap-1.5"
        >
          <Plus size={14} />
          New Ticket
        </Button>
      </div>

      {ticketsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-lg bg-[var(--border-color)] animate-pulse"
            />
          ))}
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Ticket
            size={32}
            className="text-[var(--text-secondary)] mb-3"
          />
          <p className="text-sm font-medium mb-1">
            No tickets for this project
          </p>
          <p className="text-xs text-[var(--text-secondary)]">
            Create a ticket to track support requests or issues.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map((ticket: any) => (
            <div
              key={ticket.id}
              className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:border-dyndev-green/20 transition-colors group cursor-pointer"
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  navigate(`/tickets/${ticket.id}`);
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium truncate">
                    {ticket.subject}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] px-1.5 py-0',
                      ticketTypeColors[ticket.type]
                    )}
                  >
                    {ticket.type?.replace('_', ' ')}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] px-1.5 py-0',
                      ticketStatusColors[ticket.status]
                    )}
                  >
                    {ticket.status?.replace('_', ' ')}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px] px-1.5 py-0',
                      ticketPriorityColors[ticket.priority]
                    )}
                  >
                    {ticket.priority}
                  </Badge>
                </div>
                {ticket.description && (
                  <p className="text-xs text-[var(--text-secondary)] line-clamp-1 mb-1">
                    {ticket.description}
                  </p>
                )}
                <div className="flex items-center gap-3 text-[10px] text-[var(--text-secondary)]">
                  <span>#{ticket.id.slice(0, 8)}</span>
                  <span>{ticket.submitter?.name || 'Unknown'}</span>
                  {ticket._count?.comments > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageSquare size={10} />
                      {ticket._count.comments}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-[var(--text-secondary)]">
            Page {page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create ticket dialog */}
      <Dialog
        open={createTicketOpen}
        onOpenChange={(open) => {
          setCreateTicketOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Ticket</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                !newTicketSubject.trim() ||
                !newTicketDescription.trim()
              )
                return;
              try {
                await createTicket.mutateAsync({
                  projectId,
                  subject: newTicketSubject.trim(),
                  type: newTicketType,
                  priority: newTicketPriority,
                  description: newTicketDescription.trim(),
                });
                toast({
                  title: 'Ticket created',
                  description: newTicketSubject.trim(),
                  variant: 'success',
                });
                setCreateTicketOpen(false);
                resetForm();
              } catch (err: any) {
                toast({
                  title: 'Failed to create ticket',
                  description:
                    err?.message || 'An unexpected error occurred.',
                  variant: 'destructive',
                });
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="create-ticket-subject">Subject *</Label>
              <Input
                id="create-ticket-subject"
                value={newTicketSubject}
                onChange={(e) => setNewTicketSubject(e.target.value)}
                placeholder="Brief summary of the issue"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="create-ticket-type">Type</Label>
                <Select
                  value={newTicketType}
                  onValueChange={(v) =>
                    setNewTicketType(v as TicketType)
                  }
                >
                  <SelectTrigger id="create-ticket-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="change_request">
                      Change Request
                    </SelectItem>
                    <SelectItem value="general_inquiry">
                      General Inquiry
                    </SelectItem>
                    <SelectItem value="approval_request">
                      Approval Request
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="create-ticket-priority">
                  Priority
                </Label>
                <Select
                  value={newTicketPriority}
                  onValueChange={(v) =>
                    setNewTicketPriority(v as TaskPriority)
                  }
                >
                  <SelectTrigger id="create-ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-ticket-description">
                Description *
              </Label>
              <Textarea
                id="create-ticket-description"
                value={newTicketDescription}
                onChange={(e) =>
                  setNewTicketDescription(e.target.value)
                }
                placeholder="Detailed description of the issue or request"
                rows={4}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                createTicket.isPending ||
                !newTicketSubject.trim() ||
                !newTicketDescription.trim()
              }
            >
              {createTicket.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {createTicket.isPending
                ? 'Creating...'
                : 'Create Ticket'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
